import { Outlet, useLocation } from 'react-router-dom';
import {
  faBook,
  faChartBar,
  faChartLine,
  faChartPie,
  faCalendarDay,
  faArrowTrendUp,
  faArrowRightFromBracket,
  faTag,
  faUpload,
  faCog,
} from '@/fontawesome';
import PageHeader from '@/components/ui/PageHeader';
import Tabs from '@/components/ui/Tabs';
import type { TabItem } from '@/components/ui/Tabs';
import { JOURNAL_ROUTES } from '@/constants/routes';
import { APIRoutes } from '@/services/api.routes';
import { dateToRFCDateValue } from '@/utils/helpers';
import { useJournalResource } from '@/components/journal/useJournalResource';

/** Passed to the journal sub-pages so they can refresh the "fällige Reviews" badge. */
export interface JournalOutletContext {
  refreshDueReviews: () => void;
}

const TABS: { id: string; label: string; to: string; icon: TabItem['icon'] }[] = [
  { id: 'dashboard', label: 'Dashboard', to: JOURNAL_ROUTES.DASHBOARD, icon: faChartBar },
  { id: 'trades', label: 'Trades', to: JOURNAL_ROUTES.TRADES, icon: faChartLine },
  { id: 'positions', label: 'Positionen', to: JOURNAL_ROUTES.POSITIONS, icon: faChartPie },
  { id: 'calendar', label: 'Kalender', to: JOURNAL_ROUTES.CALENDAR, icon: faCalendarDay },
  { id: 'capital', label: 'Kapital', to: JOURNAL_ROUTES.CAPITAL_LADDER, icon: faArrowTrendUp },
  { id: 'assignments', label: 'Zuteilungen', to: JOURNAL_ROUTES.ASSIGNMENTS, icon: faArrowRightFromBracket },
  { id: 'reviews', label: 'Reviews', to: JOURNAL_ROUTES.REVIEWS, icon: faBook },
  { id: 'tags', label: 'Tags', to: JOURNAL_ROUTES.TAGS, icon: faTag },
  { id: 'import', label: 'Import', to: JOURNAL_ROUTES.IMPORT, icon: faUpload },
  { id: 'settings', label: 'Einstellungen', to: JOURNAL_ROUTES.SETTINGS, icon: faCog },
];

function activeTabId(pathname: string): string {
  // Longest match wins - every journal route starts with the dashboard path.
  let active = 'dashboard';
  let bestLength = 0;
  for (const tab of TABS) {
    if (pathname === tab.to || pathname.startsWith(`${tab.to}/`)) {
      if (tab.to.length > bestLength) {
        active = tab.id;
        bestLength = tab.to.length;
      }
    }
  }
  return active;
}

export default function JournalLayout() {
  const location = useLocation();

  const { data: dueReviews, reload: refreshDueReviews } = useJournalResource(
    async () => {
      const response = await APIRoutes.listJournalReviews({ status: 'OPEN', limit: 200 });
      const today = dateToRFCDateValue(new Date());
      return response.reviews.filter((r) => r.scheduledFor <= today).length;
    },
    [],
    'Fehler beim Laden der fälligen Reviews',
  );

  const items: TabItem[] = TABS.map((tab) => ({
    id: tab.id,
    label: tab.label,
    icon: tab.icon,
    to: tab.to,
    badge: tab.id === 'reviews' ? dueReviews ?? 0 : undefined,
  }));

  const outletContext: JournalOutletContext = { refreshDueReviews };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <PageHeader title="Trading Journal" icon={faBook} />
      <Tabs items={items} activeId={activeTabId(location.pathname)} />
      <Outlet context={outletContext} />
    </div>
  );
}
